import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageCircle, Briefcase, CheckCircle, ArrowRight, Send } from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHANNELS = [
  {
    key: 'support',
    Icon: Mail,
    eyebrow: 'Account & app',
    title: 'Support',
    body: 'Trouble logging in, lost progress, billing — anything that needs fixing.',
    email: 'support@levanta.life',
  },
  {
    key: 'business',
    Icon: Briefcase,
    eyebrow: 'Partnerships',
    title: 'Business',
    body: 'Collaborations, sponsorships, integrations and B2B inquiries.',
    email: 'business@levanta.life',
  },
  {
    key: 'press',
    Icon: MessageCircle,
    eyebrow: 'Journalists',
    title: 'Press',
    body: 'Interviews, quotes, briefings, podcast pitches.',
    email: 'press@levanta.life',
  },
];

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Something went wrong');
      setSuccess(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* HERO */}
      <section className="relative pt-[110px] md:pt-[170px] pb-14 md:pb-24 px-6 overflow-hidden min-h-[580px] md:min-h-[760px]">
        <AltParallaxBackdrop />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7"
            data-testid="contact-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 inline-block" />
            Get in touch
          </span>
          <h1
            className="text-4xl md:text-7xl font-black leading-[1.02] tracking-tight text-[#0b1f3a] alt-hero-glow"
          >
            Write us.{' '}
            <em className="italic font-light text-blue-600">A real human reads&nbsp;it.</em>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            Pick the right channel below for a faster reply, or use the form for
            anything that doesn&rsquo;t fit a category.
          </p>
        </div>
      </section>

      {/* CHANNELS */}
      <section className="px-6 py-20 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-3 text-center">
            Three channels
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-12">
            Pick the one that{' '}
            <em className="italic font-light text-blue-600">fits.</em>
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {CHANNELS.map(({ key, Icon, eyebrow, title, body, email }) => (
              <a
                key={key}
                href={`mailto:${email}`}
                data-testid={`contact-channel-${key}`}
                className="group rounded-3xl bg-[#fafbfd] ring-1 ring-black/5 p-7 md:p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:bg-white hover:ring-blue-200"
              >
                <span className="inline-flex w-12 h-12 rounded-2xl bg-blue-100 text-blue-600 items-center justify-center mb-5 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-200">
                  <Icon className="w-5 h-5" strokeWidth={2.25} />
                </span>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-1">
                  {eyebrow}
                </p>
                <h3 className="text-2xl md:text-3xl font-black text-[#0b1f3a] leading-tight mb-3">
                  {title}
                </h3>
                <p className="text-[#0b1f3a]/65 leading-relaxed mb-5 text-[15px]">
                  {body}
                </p>
                <span className="inline-flex items-center gap-2 text-blue-600 font-semibold text-sm group-hover:gap-3 transition-all duration-200">
                  {email}
                  <ArrowRight className="w-4 h-4" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section
        id="form"
        className="px-6 py-20 md:py-24 bg-gradient-to-b from-white to-[#eaf3fb]"
      >
        <div className="max-w-3xl mx-auto">
          {success ? (
            <div className="rounded-3xl bg-white ring-1 ring-emerald-200 shadow-xl p-10 md:p-14 text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-[#0b1f3a] mb-3">
                Message <em className="italic font-light text-blue-600">sent.</em>
              </h2>
              <p className="text-[#0b1f3a]/70 leading-relaxed mb-8 max-w-md mx-auto">
                Thanks for writing. We&rsquo;ll reply within two working days — usually
                much faster.
              </p>
              <button
                type="button"
                onClick={() => setSuccess(false)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0b1f3a] text-white text-sm font-semibold hover:bg-blue-600 transition-colors duration-200"
              >
                Send another message
              </button>
            </div>
          ) : (
            <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-xl p-8 md:p-12">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-3 text-center">
                Or use the form
              </p>
              <h2 className="text-3xl md:text-4xl font-black text-[#0b1f3a] text-center leading-tight">
                Tell us what&rsquo;s{' '}
                <em className="italic font-light text-blue-600">on your mind.</em>
              </h2>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-[#0b1f3a] mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    data-testid="contact-name"
                    className="w-full px-4 py-3 rounded-xl bg-[#fafbfd] ring-1 ring-black/5 text-[#0b1f3a] placeholder:text-[#0b1f3a]/35 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0b1f3a] mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    required
                    data-testid="contact-email"
                    className="w-full px-4 py-3 rounded-xl bg-[#fafbfd] ring-1 ring-black/5 text-[#0b1f3a] placeholder:text-[#0b1f3a]/35 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0b1f3a] mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    placeholder="What&rsquo;s on your mind?"
                    required
                    data-testid="contact-message"
                    className="w-full px-4 py-3 rounded-xl bg-[#fafbfd] ring-1 ring-black/5 text-[#0b1f3a] placeholder:text-[#0b1f3a]/35 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow resize-none"
                  />
                </div>

                {error && (
                  <div
                    data-testid="contact-error"
                    className="bg-rose-50 text-rose-600 px-4 py-3 rounded-xl text-sm ring-1 ring-rose-200"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="w-full inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-[#0b1f3a] text-white text-base font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:transform-none transition-all duration-200"
                >
                  {loading ? 'Sending…' : (
                    <>
                      <Send className="w-4 h-4" />
                      Send message
                    </>
                  )}
                </button>
                <p className="text-xs text-[#0b1f3a]/50 text-center">
                  Your data stays with us. No tracking pixels, no cross-sale.
                </p>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* CTA — pointer to FAQ. */}
      <section className="px-6 py-24 md:py-28 bg-[#0b1f3a]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-black leading-tight">
            Quick question?{' '}
            <em className="italic font-light text-blue-300">FAQ first.</em>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-white/75">
            We answered the most common ones already — usually faster than email.
          </p>
          <div className="mt-10">
            <Link
              to="/faq"
              data-testid="contact-cta-faq"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#0b1f3a] font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Read the FAQ
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <AltFooter />
    </div>
  );
};

export default Contact;
